import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SearchableSelect.css";

// SearchableSelect is a reusable typeahead dropdown: a text input that filters a list of
// options as you type. Used for the sheet-sourced name pickers in the report flow (GM
// onsite / GM remote / Vært) so long staff lists stay searchable.
//
// Props:
//   value       currently selected string ("" = none)
//   onChange    called with the picked string
//   options     array of strings to choose from (already filtered/sorted by the caller)
//   placeholder input placeholder when nothing is selected
//   ownName     optional — marked "(dig)" and floated to the top
export default function SearchableSelect({ value, onChange, options, placeholder = "Søg…", ownName }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);

    // Own name first, then the rest (deduped).
    const ordered = useMemo(() => {
        if (!ownName) return options;
        return [...options.filter(o => o === ownName), ...options.filter(o => o !== ownName)];
    }, [options, ownName]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? ordered.filter(o => o.toLowerCase().includes(q)) : ordered;
    }, [ordered, query]);

    // Close on outside click.
    useEffect(() => {
        function onDoc(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    function pick(name) {
        onChange(name);
        setQuery("");
        setOpen(false);
    }

    return (
        <div className="ss-wrap" ref={wrapRef}>
            <button type="button" className={`ss-field ${value ? "" : "placeholder"}`} onClick={() => setOpen(o => !o)}>
                {value || placeholder}
                <span className="ss-caret">▾</span>
            </button>
            {open && (
                <div className="ss-dropdown">
                    <input
                        className="ss-search"
                        type="text"
                        autoFocus
                        placeholder="Søg navn…"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && filtered.length) { e.preventDefault(); pick(filtered[0]); } }}
                    />
                    <div className="ss-list">
                        {value && (
                            <button type="button" className="ss-option clear" onClick={() => pick("")}>
                                — Ryd valg —
                            </button>
                        )}
                        {filtered.length === 0 && <div className="ss-empty">Ingen match.</div>}
                        {filtered.map(o => (
                            <button type="button" key={o}
                                className={`ss-option ${o === value ? "selected" : ""} ${o === ownName ? "own" : ""}`}
                                onClick={() => pick(o)}>
                                {o}{o === ownName ? " (dig)" : ""}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
